import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { downloadPdf } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileDown, Plus, Pencil, Trash2, Loader2, Building2 } from "lucide-react";

const dfr = (d) => (d ? d.split("-").reverse().join("/") : "—");
const VIDE = { categorie: "MATERIEL ET OUTILLAGE", designation: "", date_acquisition: "", anterieur_2009: false, valeur: "", observations: "" };

const ImmoDialog = ({ open, setOpen, item, cats, onSave }) => {
  const [f, setF] = useState(VIDE);
  useEffect(() => { setF(item ? { ...VIDE, ...item, date_acquisition: item.date_acquisition || "", valeur: String(item.valeur) } : VIDE); }, [item, open]);
  const set = (k) => (ev) => setF((s) => ({ ...s, [k]: ev.target.value }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="immo-dialog">
        <DialogHeader><DialogTitle>{item ? "Modifier l'immobilisation" : "Nouvelle immobilisation"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <label className="text-sm">Catégorie
            <Input list="immo-cats" className="mt-1" value={f.categorie} onChange={set("categorie")} data-testid="immo-categorie" />
            <datalist id="immo-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
          </label>
          <label className="text-sm">Désignation<Input className="mt-1" value={f.designation} onChange={set("designation")} data-testid="immo-designation" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">Date d'acquisition<Input type="date" className="mt-1" value={f.date_acquisition} onChange={set("date_acquisition")} data-testid="immo-date" /></label>
            <label className="text-sm">Valeur HT (DA)<Input className="mt-1 text-right" inputMode="decimal" value={f.valeur} onChange={set("valeur")} data-testid="immo-valeur" /></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.anterieur_2009} onCheckedChange={(v) => setF((s) => ({ ...s, anterieur_2009: !!v }))} data-testid="immo-anterieur" />Antérieur à 2009</label>
          <label className="text-sm">Observations<Input className="mt-1" value={f.observations} onChange={set("observations")} data-testid="immo-observations" /></label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="immo-cancel-btn">Annuler</Button>
          <Button onClick={() => onSave(f)} data-testid="immo-save-btn">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Groupe = ({ cat, items, canEdit, onEdit, onDelete }) => (
  <div className="rounded-xl border bg-card overflow-hidden" data-testid={`immo-groupe-${cat}`}>
    <div className="px-4 py-2.5 bg-muted/40 font-semibold text-sm flex justify-between"><span>{cat}</span><span className="tabular">{fmtDA(items.reduce((a, i) => a + i.valeur, 0))}</span></div>
    <div className="overflow-x-auto"><table className="w-full text-sm min-w-[560px]">
      <tbody>
        {items.map((i) => (
          <tr key={i.id} className="border-t" data-testid={`immo-row-${i.id}`}>
            <td className="px-4 py-2">{i.anterieur_2009 && <span className="text-xs font-semibold text-primary mr-2">ANTÉRIEUR À 2009</span>}{i.designation || (i.anterieur_2009 ? "" : "—")}</td>
            <td className="px-4 py-2 w-32 tabular">{dfr(i.date_acquisition)}</td>
            <td className="px-4 py-2 w-40 text-right tabular font-medium whitespace-nowrap">{fmtDA(i.valeur)}</td>
            {canEdit && <td className="px-2 w-20 text-right whitespace-nowrap">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(i)} data-testid={`immo-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => onDelete(i)} data-testid={`immo-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
            </td>}
          </tr>
        ))}
      </tbody>
    </table></div>
  </div>
);

export default function Immobilisations() {
  const { societes, societeId } = useApp();
  const { role } = useAuth();
  const canEdit = ["administrateur", "comptabilite"].includes(role);
  const [sid, setSid] = useState("");
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [arrete, setArrete] = useState(new Date().toISOString().slice(0, 10));
  const [dlg, setDlg] = useState({ open: false, item: null });

  useEffect(() => { setSid(societeId !== "all" ? societeId : (societes[0]?.id || "")); }, [societeId, societes]);
  useEffect(() => { api.get("/immobilisations/categories").then((r) => setCats(r.data)); }, []);
  const load = () => { if (sid) { setItems(null); api.get("/immobilisations", { params: { societe_id: sid } }).then((r) => setItems(r.data)); } };
  useEffect(load, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (f) => {
    const valeur = Number(String(f.valeur).replace(/\s/g, "").replace(",", "."));
    if (Number.isNaN(valeur) || valeur < 0 || !f.categorie.trim()) return toast.error("Catégorie et valeur requises");
    const body = { ...f, societe_id: sid, valeur, date_acquisition: f.date_acquisition || null };
    try {
      if (dlg.item) await api.put(`/immobilisations/${dlg.item.id}`, body); else await api.post("/immobilisations", body);
      toast.success("Immobilisation enregistrée");
      setDlg({ open: false, item: null });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };
  const del = async (i) => {
    if (!window.confirm(`Supprimer « ${i.designation || i.categorie} » (${fmtDA(i.valeur)}) ?`)) return;
    await api.delete(`/immobilisations/${i.id}`, { params: { societe_id: sid } });
    toast.success("Immobilisation supprimée");
    load();
  };
  const groupes = [...new Set((items || []).map((i) => i.categorie))];
  const total = (items || []).reduce((a, i) => a + i.valeur, 0);

  return (
    <div className="space-y-5" data-testid="immobilisations-page">
      <PageHeader title="Immobilisations" subtitle="Tableau des investissements : biens, date et valeur d'acquisition"
        actions={<div className="flex flex-wrap items-center justify-end gap-2">
          <Select value={sid} onValueChange={setSid}>
            <SelectTrigger className="w-56" data-testid="immo-societe-select"><SelectValue placeholder="Société" /></SelectTrigger>
            <SelectContent>{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" className="w-40" value={arrete} onChange={(ev) => setArrete(ev.target.value)} data-testid="immo-date-arrete" title="Date d'arrêté du tableau (PDF)" />
          <Button variant="outline" className="gap-2" disabled={!items?.length} onClick={() => downloadPdf(`/immobilisations/pdf?societe_id=${sid}&date_arrete=${arrete}`, `tableau-investissements-${arrete}`)} data-testid="immo-pdf-btn"><FileDown className="h-4 w-4" /> PDF</Button>
          {canEdit && <Button className="gap-2" onClick={() => setDlg({ open: true, item: null })} data-testid="immo-add-btn"><Plus className="h-4 w-4" /> Ajouter</Button>}
        </div>} />
      {!items ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : !items.length ? <EmptyState icon={Building2} title="Aucune immobilisation" />
          : <>
            {groupes.map((c) => <Groupe key={c} cat={c} items={items.filter((i) => i.categorie === c)} canEdit={canEdit} onEdit={(i) => setDlg({ open: true, item: i })} onDelete={del} />)}
            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 px-4 py-3 flex justify-between font-semibold" data-testid="immo-total">
              <span>TOTAL ({items.length} biens)</span><span className="tabular">{fmtDA(total)}</span>
            </div>
          </>}
      <ImmoDialog open={dlg.open} setOpen={(o) => setDlg((d) => ({ ...d, open: o }))} item={dlg.item} cats={cats} onSave={save} />
    </div>
  );
}
